<template>
  <div id="main-container" class="d-flex flex-column justify-content-between">
    <div>
      <div id="header" class="d-flex flex-column justify-content-start">
        <div id="warning" class="d-flex flex-row mt-4 align-items-start gap-3">
          <img src="@/assets/images/icons/warning.svg" alt="warning" />
          <h4 class="align-self-end">
            {{ $t("dashboard.booking.warning") }}
          </h4>
        </div>
      </div>
      <div
        id="filter-container"
        class="d-flex flex-row justify-content-start mt-4 gap-2"
      >
        <div class="data-filter">
          <input
            name="date"
            type="text"
            onmouseover="(this.type='date')"
            onmouseleave="if(!this.value)(this.type='text')"
            onblur="if(!this.value)this.type='text'"
            v-model="startDateFilter"
            :placeholder="$t('utils.placeholder.start-date')"
            @change="getPayments"
          />
        </div>
        <div class="data-filter">
          <input
            name="date"
            type="text"
            onmouseover="(this.type='date')"
            onmouseleave="if(!this.value)(this.type='text')"
            onfocus="(this.type='date')"
            onblur="if(!this.value)this.type='text'"
            v-model="endDateFilter"
            :placeholder="$t('utils.placeholder.end-date')"
            @change="getPayments"
          />
        </div>
        <div id="status-filter">
          <select v-model="statusSelected" @change="getPayments">
            <option
              v-for="option in statusOptions"
              :value="option.value"
              :key="option.id"
            >
              {{ option.text }}
            </option>
          </select>
        </div>
      </div>
      <div id="booking-table">
        <b-table
          striped
          hover
          :items="bookings"
          :fields="fields"
          :busy="loading"
          @sort-changed="sort()"
        >
          <template #table-busy>
            <div class="text-center text-secondary my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>

          <template #cell(reject)="row" v-if="!isAdmin">
            <button
              @click="reject(row.item)"
              v-if="!row.item.reject"
              class="btn p-0"
            >
              {{ $t("utils.button.reject") }}
            </button>
          </template>

          <template #cell(delete)="row" v-if="isAdmin">
            <button
              @click="reject(row.item)"
              v-if="!row.item.reject"
              class="btn p-0"
            >
              {{ $t("utils.button.delete-booking") }}
            </button>
          </template>
        </b-table>
      </div>

      <div
        id="empty-booking"
        v-if="!loading && !bookings.length"
        class="d-flex justify-content-center align-items-center"
      >
        <p>{{ $t("dashboard.booking.empty") }}</p>
      </div>
    </div>

    <div class="pagination d-flex align-items-center justify-content-end p-4">
      <button
        class="btn d-flex align-items-center"
        @click="changePage(false)"
        :disabled="currentPage === 1 || loading"
      >
        <img
          src="@/assets/images/icons/arrow-select.svg"
          alt="Previous page"
          class="prev"
        />
      </button>
      <button
        class="btn d-flex align-items-center"
        @click="changePage(true)"
        :disabled="disableNextPage || loading"
      >
        <img
          src="@/assets/images/icons/arrow-select.svg"
          alt="Next page"
          class="next"
        />
      </button>
    </div>

    <MessageBanner
      :message="error"
      :type="'error'"
      :classes="['position-bottom-center']"
      v-if="error !== ''"
    />
  </div>
</template>

<script>
import PaymentsService from "@/services/payments";

export default {
  props: ["fields", "isAdmin"],
  data() {
    return {
      startDateFilter: "",
      endDateFilter: "",
      statusSelected: "null",
      type: "",
      items: [],
      bookings: [],
      statusOptions: [
        { text: this.$t("utils.select.all"), value: "null" },
        { text: this.$t("utils.select.refunded"), value: true },
        { text: this.$t("utils.select.paid"), value: false }
      ],
      loading: false,
      error: "",
      busy: false,
      currentPage: 1,
      disableNextPage: false,
      sortDirection: "asc"
    };
  },
  mounted() {
    this.getPayments();
  },
  methods: {
    async getPayments() {
      this.loading = true;
      let startDate = this.startDateFilter
        ? new Date(this.startDateFilter).toISOString()
        : null;

      let endDate = this.endDateFilter
        ? new Date(this.endDateFilter).toISOString()
        : null;

      try {
        const filters = {
          startDate,
          endDate,
          refund: this.statusSelected,
          orderDirection: this.sortDirection,
          orderBy: "bookingDate"
        };

        const { data } = await PaymentsService.getPayments(
          filters,
          this.currentPage
        );

        this.items = data.data;

        this.disableNextPage = this.items.length !== data.pagination.pagelen;

        this.bookings = this.mapBookings(this.items);
      } catch (error) {
        this.error = error;
        setTimeout(() => {
          this.error = "";
        }, 2500);
      } finally {
        this.loading = false;
        this.busy = false;
      }
    },

    mapBookings(data) {
      return data.map(payment => ({
        id: payment.id,
        name: payment.user.name,
        buyer: payment.user.name,
        time: payment.quantity,
        booked_at: `${this.$moment(payment.createdAt).format("DD/MM/Y HH:mm")}`,
        seat_type: this.mapSeatType(payment.product.type),
        when: this.setBookingPeriod(
          this.$moment(payment.bookingDate),
          payment.quantity
        ),
        status: this.getStatus(payment),
        confirmation_code: payment.bookingCode,
        reject: !!payment.refundId,
        paymentId: payment.paymentMethodId,
        email: payment.user.email,
        space: payment.space.name,
        city: payment.space.location.city,
        purchase_date: `${this.$moment(payment.createdAt).format(
          "DD/MM/Y HH:mm"
        )}`,
        booking_date: `${this.$moment(payment.bookingDate).format(
          "DD/MM/Y HH:mm"
        )}`
      }));
    },
    mapSeatType(seatType) {
      if (seatType === "easy_chair") {
        return this.$t("dashboard.seats.edit.easy-chair");
      } else if (seatType === "private_desk") {
        return this.$t("dashboard.seats.edit.private-desk");
      } else if (seatType === "meeting_room") {
        return this.$t("dashboard.seats.edit.meeting-rooms");
      }
    },
    getStatus(payment) {
      if (payment.refundId) {
        return "Refunded";
      } else if (payment.transactionId) {
        return "Paid (URBNX)";
      } else {
        return "Pending";
      }
    },
    setBookingPeriod(bookingDate, quantity) {
      return `
        ${bookingDate.format("DD/MM/Y HH:mm")} /
        ${bookingDate.add(quantity, "hours").format("HH:mm")}
        `;
    },
    sort() {
      if (this.sortDirection === "asc") {
        this.sortDirection = "desc";
      } else {
        this.sortDirection = "asc";
      }

      this.currentPage = 1;
      this.items = [];

      this.getPayments();
    },
    changePage(isNext) {
      isNext ? this.currentPage++ : this.currentPage--;
      this.getPayments();
    },
    async reject(item) {
      this.loading = true;
      try {
        await PaymentsService.refund(item.paymentId);
        this.getPayments();
      } catch (error) {
        this.error = error.response.data.message;

        setTimeout(() => {
          this.error = "";
        }, 2500);
      } finally {
        this.loading = false;
        this.busy = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/fonts";
@import "src/assets/scss/filters";

#main-container {
  min-height: 100vh;

  #header {
    #title {
      h1 {
        font-family: $mulish;
        font-style: normal;
        font-weight: bold;
        font-size: 24px;
        line-height: 30px;
        letter-spacing: 0.3px;
        color: #252733;
      }
    }
    #warning {
      h4 {
        font-family: $mulish;
        font-style: normal;
        font-weight: 600;
        font-size: 14px;
        line-height: 20px;
        letter-spacing: 0.2px;
        color: #252733;
        margin-bottom: -2px;
      }
    }
  }

  #empty-booking {
    height: 300px;
    p {
      font-family: $mulish;
      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      text-align: center;
      letter-spacing: 0.2px;
      color: #252733;
    }
  }

  #booking-table {
    td {
      button {
        font-family: $mulish;
        color: #ff2d2d;
        text-transform: uppercase;
        font-weight: bold;
        font-size: 13px;
        line-height: 16px;
        letter-spacing: 0.3px;

        &:focus {
          box-shadow: none;
        }
      }
    }
  }

  .pagination {
    button {
      .prev {
        transform: rotate(90deg);
      }

      .next {
        transform: rotate(-90deg);
      }

      &:focus {
        box-shadow: none;
      }
    }
  }
}
</style>

<style lang="scss">
@import "src/assets/scss/fonts";
@import "src/assets/scss/components/table";
#booking-table {
  td {
    &.name {
      font-family: $mulish;
      font-style: normal;
      font-weight: bold;
      font-size: 13px;
      line-height: 16px;
      letter-spacing: 0.3px;
      color: #252733;
    }

    &.delete {
      button {
        color: #7e4ee2 !important;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/bookings/BookingTable.vue